import { motion } from 'framer-motion'
import { ArrowDown, Cloud, Rocket, Play } from 'lucide-react'
import profile from '../data/profile.json'

// Photo de profil - remplace par ton image dans /public/photo.jpg
const PROFILE_PHOTO = '/photo.jpg'

export default function Hero() {
  const scrollToAbout = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative min-h-screen overflow-hidden bg-gradient-to-br from-gray-50 via-white to-primary-50/30">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-rose-valley-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary-100/10 to-rose-valley-100/10 rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]" />

      <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center min-h-screen py-20 text-center lg:py-32">
          {/* Profile Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative mb-8"
          >
            <div className="relative">
              {/* Gradient Ring */}
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-600 via-rose-valley-500 to-primary-600 blur-sm opacity-75 animate-gradient" />
              <div className="relative w-32 h-32 overflow-hidden border-4 border-white rounded-full shadow-2xl sm:w-40 sm:h-40">
                <img
                  src={PROFILE_PHOTO}
                  alt={profile.identity.name}
                  className="object-cover object-center w-full h-full"
                  onError={(e) => {
                    // Fallback si pas de photo
                    e.target.style.display = 'none'
                    e.target.nextSibling.style.display = 'flex'
                  }}
                />
                <div 
                  className="items-center justify-center hidden w-full h-full text-4xl font-bold text-white bg-gradient-to-br from-primary-600 to-rose-valley-500 font-display"
                >
                  {profile.identity.name.split(' ').map(n => n[0]).join('')}
                </div>
              </div>
              {/* Status Indicator */}
              <div className="absolute bottom-2 right-2 w-5 h-5 bg-green-500 border-2 border-white rounded-full shadow-lg" />
            </div>
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6"
          >
            <span className="inline-flex items-center px-4 py-2 space-x-2 text-sm font-medium rounded-full bg-primary-50 text-primary-700 ring-1 ring-primary-100">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-primary-400" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-primary-500" />
              </span>
              <span>Ravi de vous voir ici !</span>
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-4xl font-bold leading-tight tracking-tight text-balance sm:text-5xl md:text-6xl lg:text-7xl"
          >
            <span className="block text-gray-900">{profile.identity.name}</span>
            <span className="block mt-2 gradient-text">{profile.identity.title}</span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-2xl mt-6 text-lg text-gray-600 sm:text-xl text-balance"
          >
            {profile.identity.tagline}
          </motion.p>

          {/* Dual Identity Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col items-center gap-4 mt-10 sm:flex-row"
          >
            <div className="flex items-center px-5 py-3 space-x-3 bg-white border border-gray-100 shadow-sm rounded-2xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                <Cloud className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900">Expert Cloud DevOps</p>
                <p className="text-xs text-gray-500">{profile.identity.experience.years} ans d'expérience</p>
              </div>
            </div>
            <div className="flex items-center px-5 py-3 space-x-3 bg-white border border-gray-100 shadow-sm rounded-2xl">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-rose-valley-500 to-primary-600">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-900">Fondateur & CEO</p>
                <p className="text-xs text-gray-500">Groupe Octego</p>
              </div>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col items-center gap-4 mt-10 sm:flex-row"
          >
            <motion.a
              href="https://app.cal.eu/abderrahime/15min"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-primary"
            >
              Prendre rendez-vous
            </motion.a>
            <motion.a
              href="/podcast"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-secondary"
            >
              <Play className="w-4 h-4 mr-2" />
              Découvrir le podcast
            </motion.a>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.button
            onClick={scrollToAbout}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="absolute transform -translate-x-1/2 bottom-10 left-1/2"
            aria-label="Scroll to about section"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              className="flex items-center justify-center w-10 h-10 transition-colors border rounded-full text-gray-400 border-gray-200 hover:border-primary-300 hover:text-primary-600"
            >
              <ArrowDown className="w-5 h-5" />
            </motion.div>
          </motion.button>
        </div>
      </div>
    </section>
  )
}
